import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store/store'
import { resendVerification } from '@/store/slices/authSlice'
import { FiMail, FiX, FiRefreshCw } from 'react-icons/fi'

const EmailVerificationBanner: React.FC = () => {
    const { isAuthenticated, user, isEmailVerified, isLoading } = useSelector((state: RootState) => state.auth)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const [isDismissed, setIsDismissed] = useState(false)
    const [isResending, setIsResending] = useState(false)

    // Don't show if user is not authenticated, email is verified, or banner is dismissed
    if (!isAuthenticated || !user || isEmailVerified || user.emailVerified || isDismissed) {
        return null
    }

    const handleResendVerification = async () => {
        setIsResending(true)
        try {
            const response = await dispatch(resendVerification()).unwrap()
            navigate('/verify-email', {
                state: {
                    email: user?.email || '',
                    verificationCode: response?.verificationCode ?? null
                }
            })
        } catch (error) {
            // Error is handled by the slice
        } finally {
            setIsResending(false)
        }
    }

    const handleDismiss = () => {
        setIsDismissed(true)
    }

    return (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between py-3">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <FiMail className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="ml-3 flex-1">
                            <p className="text-sm text-yellow-800 dark:text-yellow-300">
                                <span className="font-medium">Verify your email address</span> to post gigs, apply for jobs, and unlock all platform features.{' '}
                                <button
                                    onClick={handleResendVerification}
                                    disabled={isResending || isLoading}
                                    className="font-medium underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                >
                                    {isResending ? (
                                        <>
                                            <FiRefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Resend verification email'
                                    )}
                                </button>
                            </p>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleDismiss}
                            className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-1.5 text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                        >
                            <span className="sr-only">Dismiss</span>
                            <FiX className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EmailVerificationBanner